import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiPhone, FiMail, FiMapPin, FiGlobe } from 'react-icons/fi';

export default function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Stay In Touch', path: '/stay-in-touch' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans text-gray-900">
      {/* Top Contact Bar */}
      <div className="bg-slate-900 text-slate-300 py-2 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex space-x-6">
            <a href="mailto:andrewkhong30@gmail.com" className="flex items-center hover:text-white transition-colors">
              <SafeIcon icon={FiMail} className="mr-2" />
              andrewkhong30@gmail.com
            </a>
            <a href="tel:+12512352120" className="flex items-center hover:text-white transition-colors">
              <SafeIcon icon={FiPhone} className="mr-2" />
              +1 (251) 235-2120
            </a>
          </div>
          <div className="flex items-center">
            <SafeIcon icon={FiMapPin} className="mr-2" />
            4641 Hermitage Rd, Mobile AL 36619
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-blue-500/30 transition-all duration-300 transform group-hover:scale-105">
                  <SafeIcon icon={FiGlobe} className="text-xl" />
                </div>
                <span className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  World Class<span className="text-blue-600 font-medium ml-1">Ventures</span>
                </span>
              </Link>
            </div>
            <div className="hidden md:flex md:items-center md:space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive(link.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      <main className="flex-grow">
        <Outlet />
      </main>

      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center text-white shadow-md">
                <SafeIcon icon={FiGlobe} className="text-base" />
              </div>
              <h3 className="text-white text-xl font-bold tracking-tight">World Class <span className="font-medium text-blue-400">Ventures</span></h3>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              Helping homeowners and business owners educate themselves on mortgages, bookkeeping and getting started in the real estate industry.
            </p>
          </div>
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/stay-in-touch" className="hover:text-white transition-colors">Stay In Touch</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Legal & Privacy</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            </ul>
            <div className="mt-6 text-sm text-slate-400">
              <p>Andy Khong</p>
              <p>andrewkhong30@gmail.com</p>
              <p>+1 (251) 235-2120</p>
              <p>4641 Hermitage Rd, Mobile AL 36619</p>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-8 border-t border-slate-800 text-sm text-center text-slate-500">
          © 2026 World Class Ventures LLC. All rights reserved.
        </div>
      </footer>
    </div>
  );
}